import React, { useContext, useEffect, useReducer, useState } from "react";

import {
  Box,
  Button,
  Card,
  Container,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Chip
} from '@mui/material';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import Edit from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import ColorLensIcon from '@mui/icons-material/ColorLens';

import ConfirmationModal from "../../components/ConfirmationModal";
import CreateOrEditCauseModal from "../../components/CreateOrEditCauseModal";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
// routes
import api from "../../services/api";
// hooks
// components
import HeaderBreadcrumbs from '../../components/HeaderBreadcrumbs';
import Iconify from '../../components/Iconify';
import Page from '../../components/Page';
import Scrollbar from '../../components/Scrollbar';
import SearchNotFound from '../../components/SearchNotFound';
// sections
import socket from "../../hooks/useSocket";
import ToastError from "../../toast/error/toastError";
import ToastSuccess from "../../toast/success/toastSuccess";


const reducer = (state, action) => {
  if (action.type === "LOAD_CAUSES") {
    return [...action.payload];
  }

  if (action.type === "UPDATE_CAUSES") {
    const cause = action.payload;
    const causeIndex = state.findIndex((c) => c.id === cause.id);

    if (causeIndex !== -1) {
      const next = [...state];
      next[causeIndex] = cause;
      return next;
    } else {
      return [cause, ...state];
    }
  }

  if (action.type === "DELETE_CAUSES") {
    const causeId = action.payload;
    return state.filter((c) => c.id !== causeId);
  }

  if (action.type === "RESET") {
    return [];
  }

  return state;
};

const Causes = () => {
  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [causes, dispatch] = useReducer(reducer, []);
  const [selectedCause, setSelectedCause] = useState(null);
  const [causeModalOpen, setCauseModalOpen] = useState(false);
  const [deletingCause, setDeletingCause] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [totalCount, setTotalCount] = useState(0);

  const { user } = useContext(AuthContext);
  const companyId = user.companyId;

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const isNotFound = !loading && !causes.length && Boolean(searchParam);

  useEffect(() => {
    setPage(0);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchCauses = async () => {
        try {
          const { data } = await api.get("/causes/", {
            params: {
              searchParam,
              pageNumber: page + 1,
              pageSize: rowsPerPage,
            },
          });
          dispatch({ type: "LOAD_CAUSES", payload: data.causes });
          setTotalCount(data.count);
          setLoading(false);
        } catch (err) {
          setLoading(false);
          ToastError(err);
        }
      };
      fetchCauses();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, page, rowsPerPage]);

  useEffect(() => {
    const handleCauseEvent = (data) => {
      if (data.action === "update" || data.action === "create") {
        if (!(companyId === data.cause.companyId)) return;
        dispatch({ type: "UPDATE_CAUSES", payload: data.cause });
        if (data.action === "create") {
          setTotalCount((prev) => prev + 1);
        }
      }

      if (data.action === "delete") {
        dispatch({
          type: "DELETE_CAUSES",
          payload: +data.causeId,
        });
        setTotalCount((prev) => Math.max(0, prev - 1));
      }
    };

    socket.on("cause", handleCauseEvent);
    return () => {
      socket.off("cause", handleCauseEvent);
    };
  }, [companyId]);

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleOpenCauseModal = () => {
    setSelectedCause(null);
    setCauseModalOpen(true);
  };

  const handleCloseCauseModal = () => {
    setSelectedCause(null);
    setCauseModalOpen(false);
  };

  const handleEditCause = (cause) => {
    setSelectedCause(cause);
    setCauseModalOpen(true);
  };

  const handleDeleteCause = async (causeId) => {
    try {
      await api.delete(`/causes/${causeId}`);
      ToastSuccess("Causa deletada com sucesso!");
    } catch (err) {
      ToastError(err);
    }
    setDeletingCause(null);
  };

  return (
    <>
      <div style={{ marginTop: '65px' }}>
        <ConfirmationModal
          title={
            deletingCause &&
            `Deletar causa ${deletingCause.name}?`
          }
          open={confirmModalOpen}
          onClose={setConfirmModalOpen}
          onConfirm={() => handleDeleteCause(deletingCause.id)}
        >
          Tem certeza que deseja deletar esta causa?
        </ConfirmationModal>
        <CreateOrEditCauseModal
          open={causeModalOpen}
          onClose={handleCloseCauseModal}
          causeId={selectedCause && selectedCause.id}
        />
        <Page title="Causas de Atendimento">
        <Container maxWidth={false}>
            <HeaderBreadcrumbs
              heading="Causas de Atendimento"
              action={
                <>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Iconify icon={'eva:plus-fill'} />}
                    onClick={handleOpenCauseModal}
                  >
                    Adicionar Causa
                  </Button>
                </>
              }
            />
            <Card>
              <TextField
                style={{ padding: '20px' }}
                placeholder="Pesquisar causas..."
                type="search"
                value={searchParam}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon style={{ color: "gray" }} />
                    </InputAdornment>
                  ),
                }}
              />
              <Scrollbar>
                <TableContainer sx={{ minWidth: 800 }}>
                  <Table >
                    <TableHead>
                      <TableRow>
                        <TableCell align="center">Nome da Causa</TableCell>
                        <TableCell align="center">Cor</TableCell>
                        <TableCell align="left">Descrição</TableCell>
                        <TableCell align="center">Ações</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {causes.map((row) => {
                        const { id, name, color, description } = row;
                        return (
                          <TableRow
                            hover
                            key={id}
                            tabIndex={-1}
                          >
                            <TableCell align="center">{name}</TableCell>
                            <TableCell align="center">
                              <Chip
                                icon={<ColorLensIcon />}
                                label={color}
                                style={{
                                  backgroundColor: color,
                                  color: getContrastColor(color),
                                  fontWeight: 'bold'
                                }}
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="left" sx={{ maxWidth: 360 }}>
                              {description ? (
                                <Tooltip
                                  title={
                                    <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap' }}>
                                      {description}
                                    </Typography>
                                  }
                                  arrow
                                  placement="top"
                                >
                                  <Box
                                    component="span"
                                    sx={{
                                      display: '-webkit-box',
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: 'vertical',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      cursor: 'help',
                                      color: 'text.primary',
                                      fontSize: '0.875rem',
                                      lineHeight: 1.45,
                                    }}
                                  >
                                    {description}
                                  </Box>
                                </Tooltip>
                              ) : (
                                <Typography
                                  variant="caption"
                                  sx={{ color: 'text.disabled', fontStyle: 'italic' }}
                                >
                                  Sem descrição
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="center">
                              <IconButton
                                size="small"
                                onClick={() => handleEditCause(row)}
                              >
                                <Edit />
                              </IconButton>

                              <IconButton
                                size="small"
                                onClick={() => {
                                  setConfirmModalOpen(true);
                                  setDeletingCause(row);
                                }}
                              >
                                <DeleteOutline />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {loading && <TableRowSkeleton columns={4} />}
                    </TableBody>
                    {isNotFound && (
                      <TableBody>
                        <TableRow>
                          <TableCell align="center" colSpan={4} sx={{ py: 3 }}>
                            <SearchNotFound searchQuery={searchParam} />
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    )}
                  </Table>
                </TableContainer>
              </Scrollbar>
              <TablePagination style={{ marginTop: 15 }}
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} ${i18n.t("userType.translate.of")} ${count}`}
                labelRowsPerPage={i18n.t("userType.translate.resultsPerPage")}
                rowsPerPageOptions={[5, 20, 40, 60, 80, 100]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(_event, newPage) => setPage(newPage)}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Card>
          </Container>
        </Page>
      </div>
    </>
  );
};

export default Causes;

function getContrastColor(hexcolor) {
  const r = parseInt(hexcolor.substr(1, 2), 16);
  const g = parseInt(hexcolor.substr(3, 2), 16);
  const b = parseInt(hexcolor.substr(5, 2), 16);
  const brightness = ((r * 299) + (g * 587) + (b * 114)) / 1000;
  return brightness > 155 ? '#000000' : '#ffffff';
}